<script>
    import { onMount } from "svelte";
    import { navigate } from "svelte-routing";
    import anime from "animejs";
    import Color from "color";
    import BoxShadow from "./BoxShadow";
    import { breatheState } from "./dataStore";

    import { colors, durations, techniques } from "./constants";

    if (!localStorage.currentDuration) {
        localStorage.currentDuration = durations[0];
    }
    const currentDuration = localStorage.currentDuration;

    if (!localStorage.currentTechniqueId) {
        localStorage.currentTechniqueId = techniques[0].id;
    }
    const currentTechniqueId = localStorage.currentTechniqueId;
    const currentTechnique = techniques.find(
        (technique) => technique.id === currentTechniqueId
    );

    const changeBreatheState = (breatheStateVal) => {
        breatheState.update((val) => (val = breatheStateVal));
    };

    const successColor = Color(colors["success"]);
    const warningColor = Color(colors["warning"]);
    const dangerColor = Color(colors["danger"]);
    let boxShadowMinRadius = "0vw";
    let boxShadowMaxRadius = "25vw";

    if (window.innerWidth > window.innerHeight) {
        boxShadowMinRadius = "0vh";
        boxShadowMaxRadius = "25vh";
    }

    const inhaleBoxShadow = new BoxShadow(
        0,
        0,
        0,
        boxShadowMinRadius,
        successColor.alpha(0.2)
    );
    inhaleBoxShadow.addShadow(
        0,
        0,
        0,
        boxShadowMaxRadius,
        successColor.alpha(0.1)
    );

    const holdBoxShadow = new BoxShadow(
        0,
        0,
        0,
        boxShadowMinRadius,
        warningColor.alpha(0.2)
    );
    holdBoxShadow.addShadow(
        0,
        0,
        0,
        boxShadowMaxRadius,
        warningColor.alpha(0.1)
    );

    const exhaleBoxShadow = new BoxShadow(
        0,
        0,
        0,
        boxShadowMaxRadius,
        dangerColor.alpha(0.2)
    );
    exhaleBoxShadow.addShadow(
        0,
        0,
        0,
        boxShadowMaxRadius,
        dangerColor.alpha(0.1)
    );

    const inactiveBoxShadow = new BoxShadow(0, 0, 0, 0, "transparent");

    let playing = false;
    /**
     * Initial hold animation
     */
    let holdAnimation = null;
    /**
     * Breathing animation
     */
    let breatheAnimationIn = null;
    let breatheAnimationOut = null;
    let exerciseTimeout = null;
    onMount(() => {
        holdAnimation = anime({
            targets: "#playBtn",
            autoplay: false,
            easing: "linear",
            keyframes: [
                {
                    boxShadow: holdBoxShadow.toString(),
                    background: colors["warning"],
                    color: colors["dark"],
                    duration: 100,
                },
                {
                    duration: 3900,
                },
            ],
            changeBegin: () => changeBreatheState("initial"),
            complete: () => breatheAnimationIn.restart(),
        });

        breatheAnimationIn = anime.timeline({
            targets: "#playBtn",
            autoplay: false,
            easing: "linear",
            loop: true,
            changeBegin: () => {
                exerciseTimeout = setTimeout(
                    animateBreathe,
                    currentDuration * 1000 * 60
                );
            },
        });
        // inhale animation
        breatheAnimationIn.add({
            boxShadow: [
                inhaleBoxShadow.toString(),
                inhaleBoxShadow
                    .getShadow(0)
                    .setSpread(boxShadowMaxRadius)
                    .getParent()
                    .toString(),
            ],
            background: [colors["success"], colors["success"]],
            color: [colors["light"], colors["light"]],
            duration: currentTechnique.pattern[0] * 1000,
            changeBegin: function (anim) {
                changeBreatheState("inhale"), console.log("inhale");
            },
        });
        // hold animation
        if (currentTechnique.pattern[1] > 0) {
            breatheAnimationIn.add({
                keyframes: [
                    {
                        boxShadow: [
                            inhaleBoxShadow.toString(),
                            holdBoxShadow
                                .getShadow(0)
                                .setSpread(boxShadowMaxRadius)
                                .getParent()
                                .toString(),
                        ],
                        background: colors["warning"],
                        color: colors["dark"],
                        duration: 100,
                    },
                    {
                        boxShadow: holdBoxShadow.toString(),
                        duration: currentTechnique.pattern[1] * 1000 - 100,
                    },
                ],
                changeBegin: function (anim) {
                    changeBreatheState("hold"), console.log("hold");
                },
            });
        }
        // exhale animation
        breatheAnimationIn.add({
            keyframes: [
                {
                    boxShadow: holdBoxShadow
                        .getShadow(0)
                        .setSpread(boxShadowMaxRadius)
                        .getParent()
                        .toString(),
                },
                {
                    boxShadow: exhaleBoxShadow.toString(),
                    background: colors["danger"],
                    color: colors["light"],
                    duration: 100,
                },
                {
                    boxShadow: exhaleBoxShadow
                        .getShadow(0)
                        .setSpread(boxShadowMinRadius)
                        .getParent()
                        .toString(),
                    duration: 3900,
                },
            ],
            changeBegin: function (anim) {
                changeBreatheState("exhale"), console.log("exhale");
            },
        });
        // hold animation
        if (currentTechnique.pattern[3] > 0) {
            breatheAnimationIn.add({
                keyframes: [
                    {
                        boxShadow: exhaleBoxShadow.toString(),
                    },
                    {
                        boxShadow: holdBoxShadow
                            .getShadow(0)
                            .setSpread(boxShadowMinRadius)
                            .getParent()
                            .toString(),
                        background: colors["warning"],
                        color: colors["dark"],
                        duration: 100,
                    },
                    {
                        boxShadow: holdBoxShadow.toString(),
                        duration: currentTechnique.pattern[3] * 1000 - 100,
                    },
                ],
                changeBegin: function (anim) {
                    changeBreatheState("hold"), console.log("hold");
                },
            });
        }

        breatheAnimationOut = anime({
            targets: "#playBtn",
            boxShadow: inactiveBoxShadow.toString(),
            background: colors["success"],
            color: colors["light"],
            duration: 100,
            begin: () => changeBreatheState("paused"),
        });
    });

    const animateBreathe = () => {
        if (!playing) {
            holdAnimation.restart();
        } else {
            holdAnimation.pause();
            breatheAnimationIn.pause();
            breatheAnimationOut.restart();
            if (exerciseTimeout !== null) {
                clearTimeout(exerciseTimeout);
            }
        }
        playing = !playing;
    };
</script>

<style>
    .play-btn,
    .helper-btn {
        cursor: pointer;
        transition: 0.2s;
    }
    .play-btn:hover,
    .helper-btn:hover {
        filter: brightness(1.2);
    }

    .play-btn {
        width: 100px;
        height: 100px;
        background-color: var(--success);
        color: var(--light);
    }
    .helper-btn {
        width: 50px;
        height: 50px;
        z-index: 1;
    }

    .hide {
        opacity: 0;
        visibility: hidden;
    }
</style>

<main
    class="flex-grow-1 d-flex flex-column flex-md-row-reverse flex-wrap justify-content-center align-items-center">
    <div class="d-flex">
        <div
            on:click={() => navigate('/techniques')}
            class="helper-btn bg-light d-flex justify-content-center align-items-center rounded-circle my-2 mx-2 {playing ? 'hide' : ''}">
            <i class="fas fa-wind fa-lg text-dark" />
        </div>
        <div
            on:click={() => navigate('/time')}
            class="helper-btn bg-transparent d-flex justify-content-center align-items-center rounded-circle my-2 mx-2 {playing ? 'hide' : ''}">
            <i class="fas fa-clock fa-3x text-light" />
        </div>
    </div>
    <div
        on:click={animateBreathe}
        id="playBtn"
        class="play-btn d-flex justify-content-center align-items-center rounded-circle my-2 mx-4">
        {#if playing}
            <i class="fas fa-pause fa-2x" />
        {:else}<i class="fas fa-play fa-2x ml-2" />{/if}
    </div>
</main>
